import React, { useState, useEffect, useRef } from 'react';
import { Button } from './button';
import { ThemeToggle } from './theme-toggle';
import { useAuth } from '../../contexts/AuthContext';
import storage from '../../lib/storage';
import oneSpaceApi from '../../lib/onespace-api';
import { Moon, Sun, TestTube } from 'lucide-react';
import { Toggle } from '@radix-ui/react-toggle';

interface SettingsMenuProps {
  onOpenSettings: () => void;
  onOpenOrgSelector: () => void;
  isAdmin?: boolean;
}

export const SettingsMenu: React.FC<SettingsMenuProps> = ({ 
  onOpenSettings, 
  onOpenOrgSelector,
  isAdmin = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { logout } = useAuth();
  const menuRef = useRef<HTMLDivElement>(null);

  // Handle clicks outside the menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node) && isOpen) {
        setIsOpen(false);
      }
    };

    // Add event listener when menu is open
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // Clean up
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleOpenSettings = () => {
    setIsOpen(false);
    onOpenSettings();
  };

  const handleOpenOrgSelector = () => {
    setIsOpen(false);
    onOpenOrgSelector();
  };

  const handleEditOrganization = async () => {
    setIsOpen(false);
    // Get the external API URL from storage
    const getExternalApiUrl = async () => {
      const localStorageJson = await storage.getItem("localStorage");
      let apiUrl = null;
      
      if (localStorageJson) {
        try {
          const parsedData = JSON.parse(localStorageJson);
          apiUrl = parsedData.tenant_api;
        } catch (error) {
          console.error("Error parsing localStorage JSON:", error);
        }
      }
      
      // If not found in combined JSON, try the new key
      if (!apiUrl) {
        apiUrl = await storage.getItem("onespace_external_api");
      }
      
      // Fallback to old key
      if (!apiUrl) {
        apiUrl = await storage.getItem("tenant_api");
      }
      
      if (apiUrl) {
        // Navigate to the dashboard endpoint
        window.open(`${apiUrl}/dashboard`, '_blank');
      } else {
        console.error("No external API URL found");
      }
    };
    
    getExternalApiUrl();
  };

  const handleLogout = () => {
    setIsOpen(false);
    logout();
  };

  const handleTestNotifications = async () => {
    setIsOpen(false);
    try {
      console.log('🧪 Testing notification system...');
      const response = await oneSpaceApi.get('/test-settings-refresh');
      console.log('✅ Test notification sent:', response.data);
    } catch (error) {
      console.error('❌ Error testing notifications:', error);
    }
  };

  return (
    <div className="fixed right-5 bottom-3 z-50" ref={menuRef}>
      {/* Menu Popup */}
      {isOpen && (
        <div className="absolute bottom-16 right-0 w-48 rounded-lg shadow-lg bg-primary-theme border border-theme-10 overflow-hidden">
          <ul className="py-2">
            {/* <li className="px-4 py-2 hover:bg-primary-theme-10 cursor-pointer text-theme" onClick={handleOpenSettings}>
              Instellingen
            </li> */}
            {isAdmin && (
              <li className="px-4 py-2 hover:bg-primary-theme-10 cursor-pointer text-theme" onClick={handleEditOrganization}>
                Bewerk Organisatie
              </li>
            )}
            <li className="px-4 py-2 hover:bg-primary-theme-10 cursor-pointer text-theme flex items-center justify-between">
              <span>Thema</span>
              <ThemeToggle small />
            </li>
            <li className="px-4 py-2 hover:bg-primary-theme-10 cursor-pointer text-theme flex items-center gap-2" onClick={handleTestNotifications}>
              <TestTube className="w-4 h-4" />
              <span>Test Notificaties</span>
            </li>
            <li className="px-4 py-2 hover:bg-primary-theme-10 cursor-pointer text-theme text-red-500" onClick={handleLogout}>
              Uitloggen
            </li>
          </ul>
        </div>
      )}
      
      {/* Cogwheel Button - larger, no background, blends with page */}
      <Button
        onClick={toggleMenu}
        className="rounded-full w-12 h-12 flex items-center justify-center
                  bg-transparent hover:bg-theme-10/20 
                  text-theme shadow-none hover:shadow-sm
                  transition-all duration-200"
        variant="ghost"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      </Button>
    </div>
  );
}; 